import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Zap, CheckCircle, XCircle, Loader } from "lucide-react";
import api from "../../api";
import { emailFromQueryParam } from "../../utils/emailFromQueryParam";
import collingsLogo from "../../assets/collingslogo.png";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const AccountVerification = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const token = query.get("token");
  const email = emailFromQueryParam(query.get("email"));

  const [status, setStatus] = useState("verifying"); // "verifying" | "success" | "error"
  const [message, setMessage] = useState("");

  const secondary = "#55C5D0";

  useEffect(() => {
    if (!token || !email) {
      setStatus("error");
      setMessage("Invalid verification link. Please request a new one.");
      return;
    }

    api
      .post("/role-management/verify_email/", { token, email })
      .then(() => {
        setStatus("success");
        setTimeout(() => navigate("/login"), 3000);
      })
      .catch((err) => {
        const detail =
          err?.response?.data?.detail || "Verification failed. The link may have expired.";
        setStatus("error");
        setMessage(detail);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4"
      style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
    >
      <div className="bg-white rounded-3xl shadow-2xl p-12 w-full max-w-lg border border-gray-100 text-center">
        {/* Logo — matches Login.js */}
        <div className="flex flex-col items-center mb-10">
          <div className="mb-3">
            <img
              src={collingsLogo}
              alt="Collings Real Estate"
              className="h-8 sm:h-9 w-auto max-w-[180px] object-contain mx-auto"
            />
          </div>
          <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600 font-medium">
            <Zap className="w-4 h-4 text-[#55C5D0]" />
            <span>AI-Powered Lead Generation Platform</span>
          </div>
          <p className="text-gray-600 font-normal text-base mt-2">
            Australia's Most Advanced Real Estate CRM
          </p>
        </div>

        {/* Status */}
        {status === "verifying" && (
          <div className="flex flex-col items-center gap-4">
            <Loader className="w-12 h-12 animate-spin" style={{ color: secondary }} />
            <p className="text-gray-600 text-base font-medium">Verifying your email…</p>
          </div>
        )}

        {status === "success" && (
          <div className="flex flex-col items-center gap-4">
            <CheckCircle className="w-14 h-14 text-green-500" />
            <h2 className="text-xl font-semibold text-gray-800">Email Verified</h2>
            <p className="text-gray-500 text-sm">
              Your email has been verified successfully. Redirecting you to login…
            </p>
            <button
              onClick={() => navigate("/login")}
              className="mt-4 w-full py-3 px-6 rounded-xl text-white font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: secondary }}
            >
              Go to Login
            </button>
          </div>
        )}

        {status === "error" && (
          <div className="flex flex-col items-center gap-4">
            <XCircle className="w-14 h-14 text-red-400" />
            <h2 className="text-xl font-semibold text-gray-800">Verification Failed</h2>
            <p className="text-gray-500 text-sm">{message}</p>
            <button
              onClick={() => navigate("/login")}
              className="mt-4 w-full py-3 px-6 rounded-xl text-white font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: secondary }}
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountVerification;
